/**
 * Pure deterministic combat transitions used by the authoritative resolver.
 * Keeping the transition relation explicit lets assurance tooling verify each
 * finite stage independently before reasoning about their composition.
 */
function isFaceCardEligible(attackerType, defenderType) {
    if (!['jack', 'queen', 'king'].includes(defenderType))
        return true;
    if (attackerType === 'queen')
        return defenderType === 'jack' || defenderType === 'queen';
    if (attackerType === 'jack')
        return defenderType === 'jack';
    return true;
}
export function resolveCardTransition(target, incomingDamage, isFrontRank, context) {
    const hpBefore = target.currentHp;
    const bonuses = [];
    if (context.modeClassicFaceCards && !isFaceCardEligible(context.attackerType, target.card.type)) {
        bonuses.push('faceCardIneligible');
        const remainingHp = context.modeDamagePersistence === 'cumulative'
            ? Math.max(target.currentHp - incomingDamage, 1)
            : target.currentHp;
        const absorbed = target.currentHp - remainingHp;
        return {
            remainingHp,
            carryover: 0,
            destroyed: false,
            step: {
                target: isFrontRank ? 'frontCard' : 'backCard',
                card: target.card,
                incomingDamage,
                hpBefore,
                effectiveHp: hpBefore,
                absorbed,
                overflow: 0,
                remaining: 0,
                damage: absorbed,
                hpAfter: remainingHp,
                destroyed: false,
                bonuses,
            },
        };
    }
    if (target.card.type === 'ace' && context.modeClassicAces) {
        if (context.attackerType === 'ace' && isFrontRank) {
            bonuses.push('aceVsAce');
            const absorbed = Math.min(incomingDamage, target.currentHp);
            const remainingHp = target.currentHp - absorbed;
            const carryover = incomingDamage - absorbed;
            const destroyed = remainingHp <= 0;
            return {
                remainingHp: destroyed ? 0 : remainingHp,
                carryover,
                destroyed,
                step: {
                    target: 'frontCard',
                    card: target.card,
                    incomingDamage,
                    hpBefore,
                    effectiveHp: hpBefore,
                    absorbed,
                    overflow: carryover,
                    remaining: carryover,
                    damage: absorbed,
                    hpAfter: destroyed ? 0 : remainingHp,
                    destroyed,
                    bonuses,
                },
            };
        }
        bonuses.push('aceInvulnerable');
        const absorbed = Math.min(incomingDamage, 1);
        const carryover = incomingDamage - absorbed;
        return {
            remainingHp: 1,
            carryover,
            destroyed: false,
            step: {
                target: isFrontRank ? 'frontCard' : 'backCard',
                card: target.card,
                incomingDamage,
                hpBefore,
                effectiveHp: hpBefore,
                absorbed,
                overflow: carryover,
                remaining: carryover,
                damage: absorbed,
                hpAfter: 1,
                destroyed: false,
                bonuses,
            },
        };
    }
    const absorbed = Math.min(incomingDamage, target.currentHp);
    const remainingHp = target.currentHp - absorbed;
    const carryover = incomingDamage - absorbed;
    const destroyed = remainingHp <= 0;
    return {
        remainingHp: destroyed ? 0 : remainingHp,
        carryover,
        destroyed,
        step: {
            target: isFrontRank ? 'frontCard' : 'backCard',
            card: target.card,
            incomingDamage,
            hpBefore,
            effectiveHp: hpBefore,
            absorbed,
            overflow: carryover,
            remaining: carryover,
            damage: absorbed,
            hpAfter: destroyed ? 0 : remainingHp,
            destroyed,
            bonuses,
        },
    };
}
export function resolveCardBoundary(input) {
    let carryover = input.carryover;
    let diamondApplied = false;
    let diamondAbsorbed = 0;
    let clubApplied = false;
    const applyDiamond = () => {
        if (input.diamondShield <= 0 || carryover <= 0)
            return;
        diamondApplied = true;
        diamondAbsorbed = Math.min(carryover, input.diamondShield);
        carryover -= diamondAbsorbed;
    };
    if (input.specVersion !== '1.0')
        applyDiamond();
    if (input.clubEligible && carryover > 0) {
        carryover *= 2;
        clubApplied = true;
    }
    if (input.specVersion === '1.0')
        applyDiamond();
    return {
        carryover,
        diamondApplied,
        diamondAbsorbed,
        clubApplied,
        clubBonusTarget: !clubApplied ? null : carryover === 0 ? 'destroyedCard' : 'nextCard',
    };
}
export function resolvePlayerBoundary(input) {
    let damage = input.carryover;
    let heartAbsorbed = 0;
    const bonuses = [];
    const applyHeart = () => {
        if (input.heartShield <= 0 || damage <= 0)
            return;
        heartAbsorbed = Math.min(damage, input.heartShield);
        damage -= heartAbsorbed;
        bonuses.push('heartDeathShield');
    };
    if (input.specVersion !== '1.0')
        applyHeart();
    if (input.spadeWeapon && damage > 0) {
        damage *= 2;
        bonuses.push('spadeDoubleLp');
    }
    if (input.specVersion === '1.0')
        applyHeart();
    return { damage, heartAbsorbed, bonuses };
}
//# sourceMappingURL=combat-math.js.map